import React, { useEffect } from 'react'
import * as THREE from 'three'
import { DEFAULT_MAX_ANGLE_DEG, fromVector3, orthonormalise } from '@/shared/sectionFrames'
import { ItemProps } from '@/components/Form/types'

import Button from '@/components/UiElements/Button'
import DeleteAbsoluteButton from '@/components/UiElements/DeleteAbsoluteButton'

import updateFromByTargetObject from '../utils/calculateSize/updateFromByTargetObject'
import cleanTargetObject from '../utils/drawSection/cleanTargetObject'
//import fixAllUVToLogo from "../utils/fixAllUVToLogo"

const SelectTargetObject: React.FC<ItemProps> = (props) => {
  const { formData, onChange, updateForm, validation } = props
  const [name, setName] = React.useState<string>('')

  const [active, setActive] = React.useState<boolean>(false)

  const handleGetTargetObject = async () => {
    const threeViewer = (window as any).threeViewer
    if (threeViewer) {
      setActive(true)
      const obj = await threeViewer.getTargetObject()

      //fixAllUVToLogo(threeViewer.scene)

      updateFromByTargetObject({
        onChange: onChange || (() => {}),
        updateForm: updateForm || (() => {}),
        obj,
      })

      // On a frame profile this same click also places the section.
      //
      // The cylindrical model only ever needed the mesh here, because the section was
      // then positioned by typing an angle. A frame is positioned by the point itself,
      // and the raycast already computed it — so asking the operator to click the
      // model a second time would be asking for information we are holding.
      if ((window as any).threeViewer?.sectionModel === 'frame') {
        const hit = threeViewer.getLastPickHit?.()
        const root = threeViewer.product
        if (hit && root) {
          root.updateMatrixWorld(true)
          const inv = new THREE.Matrix4().copy(root.matrixWorld).invert()
          const origin = hit.point.clone().applyMatrix4(inv)
          const normal = hit.normal.clone().transformDirection(inv).normalize()
          // World +Y as the up hint: artwork on a grip, a slide flat or a bottle
          // wall all want the same way up; only the surface differs.
          const { up } = orthonormalise(normal, new THREE.Vector3(0, 1, 0))
          onChange?.('placement', {
            origin: fromVector3(origin),
            normal: fromVector3(normal),
            up: fromVector3(up),
            depth: 30,
            wrapRadius: 0,
            maxAngleDeg: DEFAULT_MAX_ANGLE_DEG,
          })
        }
      }

      setActive(false)
      setName(obj?.name || '')
    }
  }

  const handleRemoveTargetObject = async () => {
    await cleanTargetObject(name)
    setName('')
    onChange?.('targetObject', null)
  }

  useEffect(() => {
    if (formData.targetObject?.name !== name) {
      setName(formData.targetObject?.name || '')
    }
  }, [formData])

  return (
    <div className={'flex flex-row gap-xs '}>
      {!name ? (
        <Button
          onClick={handleGetTargetObject}
          className={`flex-1 !justify-start !flex-row gap-md ${active ? '!border-primary-500 !shadow-focus' : ''} ${
            validation ? ' !border-red-500 !bg-red-50 !text-red-700 !border-[2px] active:!shadow-red ' : ''
          }`}
          type={'alt'}
          icon={''}
          iconClassName={active ? 'animate-spin' : ''}
        >
          <span>Select from model</span>
        </Button>
      ) : (
        <div
          className={
            'flex  items-center h-[44px] flex-1 text-text-md text-gray-900 rounded-md border border-gray-300 bg-white shadow-xs select-none pl-md relative'
          }
        >
          <span className={'flex-1 truncate '}>{name}</span>
          <DeleteAbsoluteButton onClick={handleRemoveTargetObject} />
        </div>
      )}
    </div>
  )
}

export default SelectTargetObject
